import { computed } from 'vue';
import useUpc from '~/modules/upc/composables/useUpc';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import useBopsData from '~/composables/useBopsData';
import { bossPickupMessagging, checkCutOffTime, checkPickupEligibility, bopsPickupMessagging } from '~/modules/add-to-bag/utils/mcom';

/** @typedef {import('../../types').ATBSourceUtagData} ATBSourceUtagData */
/** @typedef {import('../../types').ATBBopsAvailability} ATBBopsAvailability */

/**
 * @returns {import('vue').ComputedRef<ATBSourceUtagData>}
 */
export const useATBUTagMCOM = () => {
  const { nearestStore } = useBopsData();

  const { killswitches } = useKillswitches();
  const { selectedUpcId, upcData } = useUpc();
  const selectedPrice = useProductPrice();

  /**
   * @type {import('vue').ComputedRef<import('@nuxt-common/media-card-large/app/composables/useUpc/types').UseUpc.Upc | undefined>}
   */
  const upc = computed(() => upcData.value.upcs[selectedUpcId.value]);

  /**
   * @type {import('vue').ComputedRef<ATBBopsAvailability>}
   */
  const availability = computed(() => {
    if (upc.value) {
      return { bopsAvailable: upc.value.availability.bopsAvailable, bossAvailable: upc.value.availability.bossAvailable };
    }
    return { bopsAvailable: false, bossAvailable: false };
  });

  const pickupEligibility = computed(() => {
    const { inStoreCurbsidePickupEnabled } = killswitches;
    const { deliveryDay, cutOffTime, curbsideCutOffTime, pickupCutOffTime, otherDate, details } = nearestStore.value || {};

    const checkCutOffTimeFlag = checkCutOffTime({
      otherDate,
      pickupCutOffTime,
    });
    const checkPickupEligibilityFlag = checkPickupEligibility({
      inStorePickupEligibility: details?.inStorePickupEligibility,
      curbsidePickupEligibility: details?.curbsidePickupEligibility,
    });

    const fireLinkTagBopsMessage = bopsPickupMessagging({
      checkCutOffTimeFlag,
      checkPickupEligibilityFlag,
      deliveryDay,
      cutOffTime,
      curbsideCutOffTime,
    });

    const fireLinkTagBossMessage = bossPickupMessagging({
      inStorePickupEligibility: details?.inStorePickupEligibility,
      curbsidePickupEligibility: details?.curbsidePickupEligibility,
      availabilityMessage: upc.value?.availability?.availabilityMessage,
    });

    let pickupAvailabilityUpper = '';
    let bopsPickupAvailabilityMessage = '';
    let bossPickupAvailabilityMessage = '';

    const availabilityMessage = upc.value?.availability.availabilityMessage;
    if (availabilityMessage) {
      pickupAvailabilityUpper = availabilityMessage.toUpperCase();
      bopsPickupAvailabilityMessage = ['TODAY', 'TOMORROW'].includes(pickupAvailabilityUpper) ? pickupAvailabilityUpper : 'OTHER';
      bossPickupAvailabilityMessage = pickupAvailabilityUpper.replace(/-/g, ' TO ');
    }

    const bopsOldMessage = `BOPS:${bopsPickupAvailabilityMessage}`;
    const bossOldMessage = `BOSS:${bossPickupAvailabilityMessage}`;
    const oldUnavailable = 'NA1.0';

    let pickupEligibilityStr = '';
    const { bopsAvailable, bossAvailable } = availability.value;
    if (bopsAvailable) {
      pickupEligibilityStr = inStoreCurbsidePickupEnabled ? fireLinkTagBopsMessage : bopsOldMessage;
    } else if (!bopsAvailable && bossAvailable) {
      pickupEligibilityStr = inStoreCurbsidePickupEnabled ? fireLinkTagBossMessage : bossOldMessage;
    } else {
      pickupEligibilityStr = inStoreCurbsidePickupEnabled ? 'NA' : oldUnavailable;
    }

    return pickupEligibilityStr;
  });

  return computed(() => {
    const tagData = {
      holiday_eligibility: [''],
      pdp_outfitting: ['false'], // PDP Repo shouldn't check it
      product_pricing_state: [String(selectedPrice.value?.priceTypeId || 'ZERO')],
      pickup_eligibility: [pickupEligibility.value],
    };

    return tagData;
  });
};
